import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/common/Breadcrumb';
import CustomDropdown from '@/components/common/CustomDropdown';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import PerPageSelection from '@/components/ui/tables/PerPageSelection';
import { useSelector, useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import { getAdminService, deleteAdminService } from '@/services/AdminServices';
import { cn } from '@/lib/utils';
import DataTableFilter from './component/DataTableFilter';
import DataTable from './component/DataTable';
import { getTransactionService } from '@/services/TransactionServices';
import { getPaymentService } from '@/services/PaymentService';
import PritntPdfTable from '@/components/common/PritntPdfTable';

const TransactionsPage = ({ pageTitle }) => {
    const dispatch = useDispatch();
    const { printTableRef, handleDownloadPdf } = PritntPdfTable('Transaction Data');
    const [dataSource, setDataSource] = useState([]);
    const [totalRecord, setTotalRecord] = useState(0);
    const [activeDrawer, setActiveDrawer] = useState(null);
    const [editDataId, setEditDataId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [paymentGatewayMap, setPaymentGatewayMap] = useState({});
    const filterDivRef = useRef(null);
    const [dataFilter, setDataFilter] = useState({
        search: '',
        page: 1,
        limit: 10,
        isSuperAdmin: 0,
        extra_filter: {},
    });
    const tableRef = useRef();
    const tableColumn = [
        { name: 'Transaction Id', isIncludePrint: true },
        { name: 'Date', isIncludePrint: true },
        { name: 'Payment Method', isIncludePrint: true },
        { name: 'Order No', isIncludePrint: true },
        { name: 'Amount', isIncludePrint: true },
    ];

    useEffect(() => {
        getPaymentGateways();
    }, []);

    useEffect(() => {
        getTableData();
    }, [dataFilter]);

    const getPaymentGateways = () => {
        dispatch(
            getPaymentService({
                search: '',
                page: 1,
                limit: 100,
            })
        )
            .then((res) => {
                const paymentGatewaysDataArr = res?.data?.data?.data || [];
                const gatewayMap = {};
                paymentGatewaysDataArr.forEach((element) => {
                    gatewayMap[element?._id] = element?.name || 'Cash';
                });
                setPaymentGatewayMap(gatewayMap);
            })
            .catch(({ message, errorData }) => {
                validationErrors(errorData, message);
            });
    };

    const handlePrint = () => {
        // Create an iframe dynamically
        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'absolute';
        printFrame.style.top = '-10000px';
        document.body.appendChild(printFrame);

        const printDocument = printFrame.contentDocument || printFrame.contentWindow.document;
        printDocument.open();
        printDocument.write(`
            <html>
            <head>
                <title>Print Table</title>
                <style>
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        text-align: left;
                    }
                    th, td {
                        padding: 8px;
                        border: 1px solid #ddd;
                    }
                    th {
                        background-color: #f4f4f4;
                    }
                </style>
            </head>
            <body>
                ${tableRef.current.innerHTML}
            </body>
            </html>
        `);
        printDocument.close();

        // Wait for the iframe content to load and trigger the print dialog
        printFrame.onload = () => {
            printFrame.contentWindow.print();

            // Clean up by removing the iframe after printing
            document.body.removeChild(printFrame);
        };
    };
    const handleDrawerOpen = (editId) => {
        if (editId && editId != '') {
            setEditDataId(editId);
        } else {
            setEditDataId('');
        }
        setActiveDrawer(true);
        document.body.style.overflowY = 'hidden';
    };

    const onChangeFilter = (name, value) => {
        if (name == 'extra_filter') {
            let filterObj = value;
            setDataFilter({ ...dataFilter, extra_filter: filterObj });
        } else {
            setDataFilter({ ...dataFilter, [name]: value });
        }
    };

    const handleFilterToggle = () => {
        if (filterDivRef.current) {
            const height = filterDivRef.current.scrollHeight;
            if (!isActive) {
                setIsActive(true);
                filterDivRef.current.style.height = `100%`;
            } else {
                setIsActive(false);
                filterDivRef.current.style.height = '0px';
            }
        }
    };

    const getTableData = () => {
        setIsLoading(true);
        dispatch(getTransactionService(dataFilter))
            .then((res) => {
                setTotalRecord(res?.data?.data?.total);
                setDataSource(res?.data?.data?.data);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const deleteRecord = (deleteId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then((result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                dispatch(deleteAdminService(deleteId))
                    .then((res) => {
                        Swal.fire({
                            title: 'Deleted!',
                            text: 'Your record has been deleted.',
                            icon: 'success',
                        });
                        getTableData(dataFilter);
                        setIsLoading(false);
                    })
                    .catch(({ message, errorData, statusCode }) => {
                        validationErrors(errorData, message);
                        setIsLoading(false);
                    });
            }
        });
    };

    return (
        <>
            <Breadcrumb pageTitle={pageTitle} />
            <Card>
                <CardHeader>
                    <h3 className="db-card-title">{pageTitle}</h3>
                    <div className="db-card-filter">
                        <div className="db-field-down-arrow">
                            <PerPageSelection onChangeParPage={onChangeFilter} limit={dataFilter.limit} />
                        </div>
                        <button className="db-card-filter-btn table-filter-btn" onClick={handleFilterToggle}>
                            <i className="lab lab-filter lab-font-size-14"></i>
                            <span>Filter</span>
                        </button>
                        <CustomDropdown
                            buttonText={'Export'}
                            buttonIcon={'lab-file-export'}
                            dropdownList={[
                                {
                                    icon: 'lab-printer-line',
                                    text: 'Print',
                                    dropDownAction: handleDownloadPdf,
                                },
                            ]}
                        />
                    </div>
                </CardHeader>
                <div className={cn('table-filter-div', isActive ? 'active' : '')} ref={filterDivRef}>
                    <DataTableFilter onChangeFilter={onChangeFilter} />
                </div>
                <div ref={printTableRef}>
                    <DataTable
                        isLoading={isLoading}
                        tableColumn={tableColumn}
                        dataSource={dataSource}
                        totalRecord={totalRecord}
                        dataFilter={dataFilter}
                        onChangePage={onChangeFilter}
                        handleDrawerOpen={handleDrawerOpen}
                        deleteRecord={deleteRecord}
                        tableRef={printTableRef}
                        paymentGatewayMap={paymentGatewayMap}
                    />
                </div>
            </Card>
        </>
    );
};

export default TransactionsPage;
